import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import PopupContext from "../../../PopupContext";
import { getSpanWithColor } from "../../../utils/CardUtils";
import { formatDefaultDateTime, formatPrice } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import InfinityGridOrdersCards from "../../common/cardContainers/InfinityGridOrdersCards";
import { AlertIcon } from "../../common/dialogs/DialogBoxComponent";
import FormField from "../../common/forms/FormField";
import ListComponent from "../../common/lists/ListComponent";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import OrderMissingSizesTooltip from "./ordersComponents/OrderMissingSizesTooltip";
import OrderPreview from "./ordersComponents/OrderPreview";
import FrontSystemsChangePopup, { normalizeFrontSystemsCheck } from "./ordersComponents/FrontSystemsChangePopup";
import { canOpenSaved } from "./ordersComponents/automaticCheckState";
import { hasUnresolvedOrderAction as isChecking, canStartManualCheck } from "./ordersComponents/manualCheckState";
import { useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { manualReportKey, manualReportContext, readManualReport, savedReportReview } from "./ordersComponents/manualSavedReport";
import { runFullOrderCheck } from "./ordersComponents/fullOrderCheck";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;

const StyledPopup = styled.div`
    &.destination-warehouse-popup-content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        width: 100%;
    }

    .warehouse-popup-description {
        color: var(--text-icons-gray-100, #d1d1d1);
        text-align: center;
        width: 100%;
    }

    .warehouse-popup-description-main {
        display: block;
        font-size: 14px;
        line-height: 16px;
        margin-bottom: 16px;
    }

    .warehouse-popup-description-note {
        display: block;
        font-size: 12px;
        line-height: 16px;
    }

    .warehouse-order-summary {
        background: var(--backgrounds-lines-n-600, #10141b);
        border-radius: 4px;
        color: var(--text-icons-gray-100, #d1d1d1);
        font-size: 12px;
        line-height: 14px;
        max-width: 100%;
        padding: 10px;
        text-align: left;
        width: 187px;
    }

    .warehouse-order-summary-label {
        color: var(--text-icons-gray-100, #d1d1d1);
    }

    .warehouse-order-summary-value {
        color: var(--text-icons-green-500, #00bb00);
    }

    .warehouse-dropdown-wrapper {
        width: 100%;
        margin-bottom: 32px;
        text-align: left;
    }
`;
// #endregion

// #region CONSTANTS
const DEFAULT_STATUSES_FILTER = [
    { name: "Processing", value: "Processing", selected: true },
    { name: "Ready to export", value: "New", selected: true },
    { name: "Failed to export", value: "ExportFailedByRetailBrand", selected: true },
    //---------------
    { name: "Pending rules", value: "Pending rules", selected: true, hideFromOrganize: true },
    { name: "Pending master data", value: "Pending master data", selected: true, hideFromOrganize: true },
    {
        name: "Missing sizes in size system",
        value: "Missing sizes in size system",
        selected: true,
        hideFromOrganize: true
    },
    {
        name: "Missing info",
        value: "Missing info",
        selected: true,
        hideFromOrganize: true
    },
    {
        name: "Exported",
        value: "ExportedByRetailBrand",
        selected: true,
        hideFromOrganize: true
    }
];

const ORDERS_STATUSES = DEFAULT_STATUSES_FILTER.filter(item => !item.hideFromOrganize);

export const orderStatusColors = [
    {
        colorClass: "text-blue-600",
        forValues: ["Exported"]
    },
    {
        colorClass: "text-green-500",
        forValues: ["Ready to export"]
    },
    {
        colorClass: "text-orange-500",
        forValues: [
            "Pending rules",
            "Pending master data",
            "Missing sizes in size system",
            "Missing info",
            "Processing"
        ]
    },
    {
        colorClass: "text-red-500",
        forValues: ["Failed to export", "Deleted"]
    },
    {
        colorClass: "text-gray-300",
        forValues: ["--"]
    }
];

export const ButtonsEnabledRules = (buttonName, status) => {
    const mappedStatus = getMappedStatus(status)?.toLowerCase();

    switch (buttonName?.toLowerCase()) {
        case "check":
            return mappedStatus === "ready to export";
        case "export":
            if (mappedStatus === "missing info") return false;
            if (mappedStatus === "processing") return false;
            return mappedStatus === "ready to export" || mappedStatus === "failed to export";
        case "rules":
            //Keep RULES button enabled in all order status.
            if (!mappedStatus || mappedStatus === "--") return false;
            return true;
        case "delete":
        case "view":
            return true;
        default:
            return false;
    }
};
// #endregion

// #region CHECK TRANSPORT
const checkWarehouseID = value => typeof value === "string" ? value : Number.isSafeInteger(value) ? String(value) : undefined;
export async function requestAuthorizedFrontSystemsCheck(selection) {
    const response = await Library.makeApiRequest({
        endpoint: "X/SupplierOrders/checkSupplierOrderPrices",
        data: { supplierOrderID: selection.orderID, retailBrandID: selection.retailBrandID, storeID: selection.storeID,
            ...(selection.scopeKind === "order-check/v2" ? { scopeKind: "order-check/v2" } : { warehouseID: checkWarehouseID(selection.warehouseID) }), expectedUpdatedAt: selection.expectedUpdatedAt,
            ...(selection.reportContract ? { reportContract: selection.reportContract, reportRunID: selection.reportRunID } : {}),
            checkAction: selection.checkAction || "start", ...(selection.continuationToken ? { continuationToken: selection.continuationToken } : {}),
            ...(selection.cancelToken ? { cancelToken: selection.cancelToken } : {}) },
        onErrorShowMsg: false
    });
    return response?.data?.status === 200 ? response.data.data : undefined;
}
// #endregion
export default function OrdersPage({ requestFrontSystemsCheck = requestAuthorizedFrontSystemsCheck } = {}) {
    const manualQueryClient = useQueryClient();
    document.title = `${APP_TITLE} - Orders`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [listNumRows, setListNumRows] = useState(10);
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const gridRef = useRef(null);
    const selectedDestinationWarehouseRef = useRef(null);
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const navigate = useNavigate();
    // #endregion

    // #region STATES
    const [isLoading, setIsLoading] = useState(false);
    const [frontSystemsReview, setFrontSystemsReview] = useState(null);
    const [savedCheckReview, setSavedCheckReview] = useState(null);
    const savedViewerGeneration = useRef(0);
    const automaticRowsRef = useRef(new Map());
    const checkSessionRef = useRef(null);
    const exportWorkflowRef = useRef(null);
    const exportPendingRef = useRef(false);
    const [isLoadingOrdersExport, setIsLoadingOrdersExport] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    const [allStatusList, setAllStatusList] = useState(() => ORDERS_STATUSES.map(item => ({ ...item })));
    const [filteredStatus, setFilteredStatus] = useState(() => ORDERS_STATUSES.filter(item => item.selected));
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const [showOrderPreview, setShowOrderPreview] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);

    const filters = {
        retailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        searchText: searchText,
        // Empty organizer selection removes only the additional active-status restriction.
        // Never send []: the server interprets it as all statuses, including Archive.
        statuses: (filteredStatus.length ? filteredStatus : ORDERS_STATUSES).map(item => item.value)
    };
    const gridQueryKey = ["OrdersGrid", filters];
    const listQueryKey = ["OrdersList", filters];
    // #endregion

    // #region METHODS - MAIN WINDOW HEADER
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(gridQueryKey);
            gridRef.current?.reset?.(gridQueryKey);
        }
    };

    const onSearch = text => {
        setSearchText(text);
    };

    const onRetailBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredRetailBrand(selections);
        setAllRetailBrandsList(dropdownDataWithSelections);
    };

    const onLabelBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredLabelBrand(selections);
        setAllLabelBrandsList(dropdownDataWithSelections);
    };

    const onStatusChange = selections => {
        const selected = ORDERS_STATUSES.filter(item => selections?.some(selection => selection?.value === item.value));
        if (selected.length === filteredStatus.length && selected.every((item, index) => item.value === filteredStatus[index]?.value)) return;
        setFilteredStatus(selected);
        setAllStatusList(ORDERS_STATUSES.map(item => ({ ...item, selected: selected.some(selection => selection.value === item.value) })));
    };

    const onFiltersLoaded = data => {
        //const defaultRetailBrand = sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");
        //const defaultLabelBrand = sessionStorage.getItem("currentLabelBrand") || localStorage.getItem("defaultLabelBrand");
        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }
                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultRetailBrand
                };
            });
            //const defaultRetailBrandObj = retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand(retailBrands);
        }

        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map((item, index) => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }
                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultLabelBrand
                };
            });
            //const defaultLabelBrandObj = labelBrands?.find(item => item?.value === defaultLabelBrand) || labelBrands?.[0];

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(labelBrands);
        }

        setRequestsEnabled(true);
    };
    // #endregion

    // #region METHODS - CONFIRMATION DIALOGS
    // UI context guards supplement the existing server authorization; they grant no authority.
    const exportContextKey = (row, warehouse) => JSON.stringify([
        row?._id, row?.retailBrandID, row?.storeID, row?.status, row?.updatedAt, row?.supplierOrderType,
        row?.retailBrandPosStatus, row?.storePosStatus, warehouse?.id,
        localStorage.getItem("currentCompanyID"), filters
    ]);
    const isCurrentExportWorkflow = session => {
        if (!session || exportWorkflowRef.current !== session) return false;
        const row = automaticRowsRef.current.get(session.row?._id) || session.row;
        return !isChecking(row) && (session.intent === "manual-check"
            ? canStartManualCheck(row, getMappedStatus(row?.status)) : ButtonsEnabledRules("export", row?.status)) &&
            exportContextKey(row, session.warehouse) === session.contextKey;
    };
    const cancelFrontSystemsCheck = session => {
        if (!session?.checkContext?.reportRunID || session.cancelPending || session.cancelAttempts >= 2 ||
            session.cancelOutcome?.status === "uncertain" || (!session.processing && !session.serverActive)) return;
        const token = session.cancelToken;
        if (session.cancelOutcome && !(token && token !== session.cancelledToken &&
            (session.cancelOutcome.status === "acknowledged" && session.cancelOutcome.checkpoint === "deferred" ||
                session.cancelOutcome.status === "rejected" && session.cancelOutcome.reason === "REPORT_RUN_NOT_ACTIVE"))) return;
        session.cancelPending = true;
        session.cancelAttempts = (session.cancelAttempts || 0) + 1;
        session.cancelledToken = token;
        const retainOutcome = raw => {
            const outcome = raw?.cancellation;
            session.cancelOutcome = outcome?.status === "acknowledged" && outcome.publicationFenced === true
                ? { status: "acknowledged", checkpoint: ["deferred", "acknowledged", "not-active"].includes(outcome.checkpoint) ? outcome.checkpoint : "uncertain" }
                : outcome?.status === "rejected" ? { status: "rejected", reason: outcome.reason === "REPORT_RUN_NOT_ACTIVE" ? outcome.reason : "REPORT_CANCEL_REJECTED" }
                : { status: "uncertain" };
            if (session.cancelOutcome.status !== "acknowledged" || session.cancelOutcome.checkpoint === "uncertain")
                Library.showToastMessage({ type: "error", message: session.cancelOutcome.status === "rejected"
                    ? "CHECK cancellation was rejected. Saved state must be reconciled; no CHECK was resubmitted."
                    : "CHECK cancellation or checkpoint closure is uncertain. No automatic retry was submitted." });
            manualQueryClient.invalidateQueries(session.reportKey);
        };
        // A durable publication fence can precede the checkpoint handle. At most
        // one later exact-handle cleanup is allowed after a known acknowledgement
        // or definite NOT_ACTIVE rejection; uncertainty is never retried.
        requestFrontSystemsCheck({ ...session.checkContext, checkAction: "cancel", cancelToken: session.cancelToken })
            .then(retainOutcome, () => retainOutcome(null)).finally(() => {
                session.cancelPending = false;
                if (session.cancelled && session.cancelToken && session.cancelToken !== token) cancelFrontSystemsCheck(session);
            });
    };
    const closeFrontSystemsReview = () => {
        const session = checkSessionRef.current;
        if (session) {
            session.cancelled = true;
            cancelFrontSystemsCheck(session);
        }
        checkSessionRef.current = null;
        exportWorkflowRef.current = null;
        setFrontSystemsReview(null);
    };
    const beginFrontSystemsCheck = async (row, session, initialAction = "start") => {
        if (!isCurrentExportWorkflow(session) || session.stage !== "check" || !session.checkEnabled || row?.supplierOrderType !== "Bulk Order") return;
        // Only the explicit UPDATE callback may replace a saved result.
        if (!["start", "update"].includes(initialAction) || (initialAction === "update") !== !!session.savedReport?.available) return;
        session.contextKey = exportContextKey(row);
        session.exportRow = { ...row };
        session.stage = "review";
        session.processing = true;
        session.latestReport = null; session.cancelPending = false; session.cancelAttempts = 0;
        session.cancelOutcome = null; session.cancelledToken = null; session.cancelToken = null;
        session.reportKey = manualReportKey(row);
        manualQueryClient.setQueryData(session.reportKey, { ...session.savedReport, state: "running", count: null });
        checkSessionRef.current = session;
        const context = { scopeKind: "order-check/v2", orderID: row?._id, retailBrandID: row?.retailBrandID,
            storeID: row?.storeID, expectedUpdatedAt: row?.updatedAt, supplierOrderType: row?.supplierOrderType,
            reportContract: "manual-report/v1", reportRunID: uuidv4() };
        setGenericConfirmationPopup({ show: false });
        // Selected order metadata is display-only; it grants no provider or server authority.
        const selectionSummary = { orderNumber: row?.orderNumber, brand: row?.labelBrandName,
            store: row?.storeName, seasons: Array.isArray(row?.seasons) ? row.seasons.join(", ") : undefined,
            barcodes: row?.totalBarcodes, units: row?.totalOrderProducts ?? row?.totalItems };
        session.checkContext = context;
        setFrontSystemsReview({ status: "checking", processing: true, context, selectionSummary, posDisplay: session.posDisplay, manualReport: session.savedReport });
        try {
            const current = () => !session.cancelled && checkSessionRef.current === session && isCurrentExportWorkflow(session) && session.stage === "review";
            const request = async selection => {
                const raw = await requestFrontSystemsCheck({ ...selection, checkAction: selection.checkAction === "start" ? initialAction : selection.checkAction });
                if (raw?.manualReport && current()) { session.latestReport = raw.manualReport; manualQueryClient.setQueryData(session.reportKey, previous =>
                    (previous?.generation || 0) > (raw.manualReport.generation || 0) ? previous : raw.manualReport); }
                if (typeof raw?.continuation?.cancelToken === "string" && /^[a-f0-9]{64}$/.test(raw.continuation.cancelToken))
                    session.cancelToken = raw.continuation.cancelToken;
                session.serverActive = raw?.continuation?.canContinue === true;
                // A close before the first response must still cancel once its handle arrives.
                if (session.cancelled) cancelFrontSystemsCheck(session);
                return raw;
            };
            const review = await runFullOrderCheck({ request, context, session, isCurrent: current,
                normalize: normalizeFrontSystemsCheck, onProgress: review => {
                    if (current()) setFrontSystemsReview({ ...review, processing: true, context, selectionSummary, posDisplay: review.posDisplay || session.posDisplay, manualReport: session.savedReport });
                } });
            if (current()) {
                // Final normalized merge completed before the handler/UI can become eligible.
                session.processing = false;
                const report = session.latestReport;
                const saved = report?.state === "saved" && !report.error;
                if (!saved && !report?.error) manualQueryClient.setQueryData(session.reportKey, { state: "uncertain", error: "CHECK_OR_SAVE_UNVERIFIED" });
                const display = !saved && session.savedReview ? session.savedReview : review;
                session.savedReport = saved ? report : { ...session.savedReport, ...report, error: report?.error || "CHECK_OR_SAVE_UNVERIFIED" };
                if (saved) session.savedReview = review;
                setFrontSystemsReview({ ...display, processing: false, context, selectionSummary,
                    posDisplay: display.posDisplay || session.posDisplay, manualReport: session.savedReport });
            }
        } catch {
            if (checkSessionRef.current === session && isCurrentExportWorkflow(session) && session.stage === "review")
                { session.processing = false; setFrontSystemsReview({ ...(session.savedReview || { status: "incomplete" }), processing: false, context, selectionSummary, posDisplay: session.posDisplay, manualReport: { ...session.savedReport, error: "CHECK_OR_SAVE_UNVERIFIED" } }); }
        }
    };
    const dispatchExistingExport = session => {
        if (exportPendingRef.current || session?.submitted) return;
        if (!isCurrentExportWorkflow(session)) { closeFrontSystemsReview(); return; }
        session.submitted = true;
        exportPendingRef.current = true;
        checkSessionRef.current = null;
        setFrontSystemsReview(null);
        // No retry: unresolved transport outcomes retain the lock until navigation/reload.
        return exportToPOS(session.exportRow, session.exportWarehouse, session.onSuccessClosePopup)
            .finally(() => { exportPendingRef.current = false; });
    };
    const continueExistingExport = () => {
        const session = exportWorkflowRef.current;
        if (exportPendingRef.current || session?.processing || session?.cancelled || session?.invalidated || session?.stage !== "review") return;
        if (!isCurrentExportWorkflow(session)) { closeFrontSystemsReview(); return; }
        const row = automaticRowsRef.current.get(session.row?._id) || session.row;
        if (!ButtonsEnabledRules("export", row?.status)) return;
        // EXPORT from the report is an explicit new export intent, with its usual warehouse flow.
        onClickExport(row);
    };
    useEffect(() => () => { if (checkSessionRef.current) { checkSessionRef.current.cancelled = true; cancelFrontSystemsCheck(checkSessionRef.current); } checkSessionRef.current = null; exportWorkflowRef.current = null; }, []);
    useEffect(() => {
        const session = exportWorkflowRef.current;
        if (session && !session.submitted && !isCurrentExportWorkflow(session)) closeFrontSystemsReview();
    });

    const showConfirmExportDialog = (row, warehouse, session) => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            title: "Export Confirmation",
            message: `You’re about to export Order #${row?.orderNumber} with ${row?.totalBarcodes} barcodes.<br />
            If any items don’t already exist in your system, they’ll be created automatically. <br />
            Stock will remain pending until delivery. <br /><br />
            Ready to continue?`,
            headingText: "Just Checking...",
            headingColor: "var(--text-icons-blue-500, #00aeef)",
            onConfirmClicked: () => {
                dispatchExistingExport(session);
            },
            onCancelClicked: closeFrontSystemsReview,
            showConfirm: true,
            showCancel: true,
            confirmText: "CONTINUE",
            cancelText: "CANCEL",
            width: "441px",
            confirmState: "secondary"
        });
    };

    const showPOSSetupNeededDialog = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: "POS setup needed!",
            message:
                "Your POS system is not yet configured to connect to BOtag.<br />A POS connection is required to export orders.",
            type: "error",
            showConfirm: true,
            showCancel: true,
            confirmText: "POS SETUP",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                navigate("/stores/pos");
            }
        });
    };

    const showPOSConnectionNeededDialog = storeOurID => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: "POS connection needed!",
            message: "BOtag is not yet connected to this store.<br />A POS connection is required to export orders.",
            type: "error",
            showConfirm: true,
            showCancel: true,
            confirmText: "CONNECT STORE",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                navigate(`/stores/edit/${storeOurID}#stock-sharing`);
                sessionStorage.setItem("scrollToPOS", true);
            }
        });
    };
    // #endregion

    // #region METHODS - BUTTON ACTIONS AND PROPS
    const closeSavedCheck = () => { savedViewerGeneration.current++; setSavedCheckReview(null); };
    const onAutomaticCheckRowsChange = rows => {
        automaticRowsRef.current = new Map(rows.map(row => [row._id, row]));
        const session = exportWorkflowRef.current;
        if (["warehouse", "confirmation"].includes(session?.stage) && !session.submitted && isChecking(automaticRowsRef.current.get(session.row?._id))) {
            closeFrontSystemsReview();
            setGenericConfirmationPopup({ show: false });
        }
    };
    const onClickSavedCheck = async row => {
        if (!canOpenSaved(row)) return;
        const generation = ++savedViewerGeneration.current;
        const response = await Library.makeApiRequest({ endpoint: "SupplierOrders/getSupplierOrderCheckResult",
            data: { supplierOrderID: row._id, retailBrandID: row.retailBrandID, storeID: row.storeID }, onErrorShowMsg: false });
        const saved = response?.data?.status === 200 ? response.data.data : null;
        if (generation === savedViewerGeneration.current && saved?.available && saved.evidence?.version === "saved-store-check/v1")
            setSavedCheckReview(saved.evidence.review);
    };
    useEffect(() => () => { savedViewerGeneration.current++; }, []);
    const onClickPreview = row => {
        if (isChecking(row)) return;
        setOrderDetails(row);
        setShowOrderPreview(true);
    };

    const onClickManualCheck = async selected => {
        const currentRow = automaticRowsRef.current.get(selected?._id) || selected;
        const row = { ...currentRow, posDisplay: selected?.posDisplay || currentRow?.posDisplay };
        if (!canStartManualCheck(row, getMappedStatus(row?.status)) || exportPendingRef.current || isLoadingOrdersExport ||
            checkSessionRef.current?.processing || exportWorkflowRef.current?.resolvingWarehouse || exportWorkflowRef.current?.openingReport) return;
        closeFrontSystemsReview();
        if (row?.retailBrandPosStatus !== "Connected") { showPOSSetupNeededDialog(); return; }
        if (row?.storePosStatus !== "Connected") { showPOSConnectionNeededDialog(row?.storeOurID); return; }
        const session = { row, intent: "manual-check", contextKey: exportContextKey(row), stage: "review", checkEnabled: true,
            openingReport: true, posDisplay: row.posDisplay };
        exportWorkflowRef.current = session;
        checkSessionRef.current = session;
        const context = manualReportContext(row), selectionSummary = { orderNumber: row.orderNumber, brand: row.labelBrandName, store: row.storeName, barcodes: row.totalBarcodes, units: row.totalOrderProducts ?? row.totalItems };
        setFrontSystemsReview({ status: "loading-report", processing: true, context, selectionSummary, posDisplay: row.posDisplay });
        const knownReport = manualQueryClient.getQueryData(manualReportKey(row));
        try {
            const report = await readManualReport(row);
            if ((knownReport?.available && !report.available) || report.generation < (knownReport?.generation || 0))
                throw Error("SAVED_REPORT_REGRESSED");
            if (session.cancelled || checkSessionRef.current !== session || !isCurrentExportWorkflow(session)) return;
            session.openingReport = false;
            manualQueryClient.setQueryData(manualReportKey(row), report);
            session.savedReport = report;
            session.posDisplay = report.posDisplay || session.posDisplay;
            if (report.available) {
                const review = savedReportReview(report, context, selectionSummary, session.posDisplay);
                session.savedReview = review;
                setFrontSystemsReview(review);
            } else if (["running", "uncertain"].includes(report.state)) {
                setFrontSystemsReview({ status: "incomplete", context, selectionSummary, posDisplay: row.posDisplay,
                    message: "A prior CHECK has an unresolved outcome. No new CHECK started.", manualReport: report });
            } else { session.stage = "check"; beginFrontSystemsCheck(row, session); }
        } catch {
            session.openingReport = false;
            if (checkSessionRef.current === session && !session.cancelled) setFrontSystemsReview({ status: "incomplete", context, selectionSummary,
                posDisplay: row.posDisplay, message: "Saved CHECK status unavailable. No CHECK started.", manualReport: { error: "REPORT_READ_FAILED" } });
        }
    };

    const updateManualCheck = () => {
        const session = checkSessionRef.current;
        if (!session?.savedReport?.available || ["running", "uncertain"].includes(session.savedReport.state) || session.processing || session.openingReport || session.cancelled || !isCurrentExportWorkflow(session)) return;
        session.stage = "check";
        session.invalidated = false;
        beginFrontSystemsCheck(automaticRowsRef.current.get(session.row._id) || session.row, session, "update");
    };

    const onClickExport = row => {
        if (isChecking(row) || checkSessionRef.current?.processing) return;
        if (exportPendingRef.current || isLoadingOrdersExport ||
            ((exportWorkflowRef.current?.processing || exportWorkflowRef.current?.resolvingWarehouse) && exportWorkflowRef.current.row?._id === row?._id)) return;
        closeFrontSystemsReview();
        if (!ButtonsEnabledRules("export", row?.status)) return;
        const isPOSSetupNeeded = row?.retailBrandPosStatus !== "Connected";
        const isPOSConnectionNeeded = row?.storePosStatus !== "Connected";

        if (isPOSSetupNeeded) {
            showPOSSetupNeededDialog();
            return;
        }
        if (isPOSConnectionNeeded) {
            showPOSConnectionNeededDialog(row?.storeOurID);
            return;
        }

        const session = { row, intent: "export", warehouse: undefined, contextKey: exportContextKey(row), stage: "warehouse", checkEnabled: false, resolvingWarehouse: true };
        exportWorkflowRef.current = session;
        showDestinationWarehousePopup(row, session);
        //showConfirmExportDialog(row);
    };

    const exportToPOS = async (row, warehouse, onSuccessClosePopup = false) => {
        const payload = {
            retailBrandID: row?.retailBrandID,
            storeID: row?.storeID,
            supplierOrderIDs: [row?._id],
            warehouseIdFromPOS: warehouse?.id
        };

        const res = await Library.makeApiRequest({
            endpoint: "X/SupplierOrders/exportSupplierOrderToPOS",
            data: payload,
            setLoader: setIsLoadingOrdersExport,
            onErrorShowMsg: false
        });

        onClickRefreshAction();

        if (res?.data?.status !== 200) {
            Library.showConfirmationMessage({
                setGenericConfirmationPopup,
                show: true,
                headingText: "ATTENTION!",
                headingColor: "var(--text-icons-orange-500, #f90)",
                title: "Order not exported!",
                message: `There was a problem exporting this order. Several causes may have prevented this order to go through. 
                          Try again in a few minutes or contact support@botag.no for assistance.`,
                type: "error",
                showConfirm: false,
                showCancel: true,
                cancelText: "CLOSE",
                titleColor: "var(--text-icons-orange-500, #f90)"
            });

            return;
        }
        const { supplierOrdersExportResult, productsExportResult } = res.data?.data || {};
        if (onSuccessClosePopup) {
            setGenericConfirmationPopup({
                show: false
            });
        }

        const createdOrdersCount = supplierOrdersExportResult?.createdCount || 0;
        const alreadyCreatedOrdersCount = supplierOrdersExportResult?.alreadyCreatedCount || 0;
        const createdProductsCount = productsExportResult?.createdCount || 0;
        const barcodesExportedCount = productsExportResult?.barcodesExported || 0;

        if (createdOrdersCount === 1) {
            const htmlMessage = `
                <div style="color: #d1d1d1; text-align: center; font-family: sans-serif;">
                    <div style="font-size: 14px; line-height: 22px;">
                        ${barcodesExportedCount} products exported.<br />
                        ${createdProductsCount} products created.
                    </div>

                    <div style="font-size: 12px; line-height: 15px; margin-top: 4px;">
                        (if products already exist in your system we don’t<br />create them again)
                    </div>

                    <div style="font-size: 14px; line-height: 22px; margin-top: 4px;">
                        Stock pending delivery.
                    </div>
                </div>`;

            Library.showConfirmationMessage({
                setGenericConfirmationPopup,
                show: true,
                headingText: "SUCCESS!",
                headingColor: "var(--text-icons-blue-500, #00aeef)",
                title: "This order is now moved to ARCHIVE",
                message: htmlMessage,
                type: "success",
                showConfirm: false,
                showCancel: true,
                cancelText: "CLOSE"
            });
        } else if (alreadyCreatedOrdersCount > 0)
            Library.showToastMessage({
                message: "Order already exported!",
                type: "error"
            });
        else
            Library.showToastMessage({
                message: "Something went wrong!",
                type: "error"
            });
    };

    // Preserve the existing exporter and payload; prepare only the guarded manual route.
    const beginExistingExport = (row, warehouse, onSuccessClosePopup, session) => {
        if (!isCurrentExportWorkflow(session) || session.stage !== "warehouse" || session.checkEnabled) return;
        session.warehouse = warehouse;
        session.contextKey = exportContextKey(row, warehouse);
        session.exportRow = { ...row };
        session.exportWarehouse = { ...warehouse };
        session.onSuccessClosePopup = onSuccessClosePopup;
        session.stage = "confirmation";
        if (onSuccessClosePopup) dispatchExistingExport(session);
        else showConfirmExportDialog(row, warehouse, session);
    };

    const showDestinationWarehousePopup = async (row, session) => {
        const res = await Library.makeApiRequest({
            endpoint: "X/SupplierOrders/getWarehousesFromPOS",
            data: { retailBrandID: row?.retailBrandID, storeID: row?.storeID },
            setLoader: setIsLoading,
            onErrorShowMsg: false
        });

        if (!isCurrentExportWorkflow(session)) return;
        session.resolvingWarehouse = false;
        if (res?.data?.status !== 200) { closeFrontSystemsReview(); return; }

        session.checkEnabled = false;
        // Response is an array of {id, name}.
        const warehouses = res?.data?.data?.warehouses || [];
        const storeWarehouseID = res?.data?.data?.storeWarehouseID || null;
        if (warehouses?.length === 0) {
            Library.showToastMessage({ message: "No warehouses found!", type: "error" });
            return;
        }

        if (warehouses?.length === 1) {
            beginExistingExport(row, warehouses[0], false, session);
            return;
        }

        const storeWarehouseExists = storeWarehouseID && warehouses.some(w => w?.id === storeWarehouseID);
        const defaultWarehouse = storeWarehouseExists
            ? warehouses.find(w => w?.id === storeWarehouseID)
            : warehouses[0];
        selectedDestinationWarehouseRef.current = defaultWarehouse;

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            useIconSrc: AlertIcon(),
            headingText: "ATTENTION!!",
            headingColor: "var(--text-icons-orange-500)",
            title: "Select destination warehouse",
            useAlternativeLayout: (
                <ChooseDestinationWarehouseContent
                    warehouseData={row}
                    warehouses={warehouses}
                    storeWarehouseID={storeWarehouseID}
                    onWarehouseChange={warehouse => {
                        if (exportWorkflowRef.current !== session) return;
                        if (session.stage !== "warehouse") { closeFrontSystemsReview(); return; }
                        selectedDestinationWarehouseRef.current = warehouse;
                    }}
                />
            ),
            confirmText: "EXPORT",
            onCancelClicked: closeFrontSystemsReview,
            cancelText: "CANCEL",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-orange-500)",
            afterConfirmWillResetPopup: false,
            onConfirmClicked: () => {
                const selectedWarehouse = selectedDestinationWarehouseRef.current ?? warehouses[0];
                if (!isCurrentExportWorkflow(session)) return;
                beginExistingExport(row, selectedWarehouse, true, session);
            }
        });
    };

    const onClickSetRules = item => {
        if (isChecking(item)) return;
        navigate(`/products/pos-export?labelBrandID=${item?.labelBrandID}`);
    };

    const onClickDelete = item => {
        if (isChecking(item)) return;
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            useIconSrc: AlertIcon(),
            headingText: "ATTENTION!!",
            headingColor: "var(--text-icons-orange-500)",
            title: "Deleting order?",
            messageHtml:
                "This order will be permanently removed from the Orders page. You will lose access to it from your account.",
            confirmText: "YES",
            cancelText: "NO",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: () => {
                deleteSupplierOrder(item, false);
            }
        });
    };

    const deleteSupplierOrder = async (item, forceDelete = false) => {
        const res = await Library.makeApiRequest({
            endpoint: "SupplierOrders/deleteBulkOrder",
            data: { supplierOrderID: item?._id, forceDelete: forceDelete },
            setLoader: setIsLoading,
            onErrorShowMsg: false
        });

        if (res?.status !== 200) return;

        Library.showToastMessage({ message: "Order deleted successfully", type: "success" });

        onClickRefreshAction();
    };

    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Order nr.",
            selector: row => row?.orderNumber ?? "--"
        },
        {
            name: "From brand",
            selector: row => row?.labelBrandName ?? "--"
        },
        {
            name: "To store",
            selector: row => row?.storeName ?? "--"
        },
        {
            name: "Imported on",
            selector: row => formatDefaultDateTime(row?.createdAt)
        },
        {
            name: "Imported from",
            selector: row => "Email" //row?.importedFromPosName ?? "--"
        },
        {
            name: "Total",
            selector: row => formatPrice(row?.totalPriceIncludingVAT, "kr", "--")
        }
    ];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            onSearch,
            onFiltersLoaded,
            allRetailBrandsList,
            onRetailBrandChange,
            filteredRetailBrand,
            allLabelBrandsList,
            onLabelBrandChange,
            filteredLabelBrand,
            allStatusList,
            onStatusChange,
            filteredStatus
        );
    }, [props?.layoutType, allRetailBrandsList, filteredRetailBrand, allLabelBrandsList, filteredLabelBrand, allStatusList, filteredStatus]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="orders-page">
            <FullScreenLoader isLoading={isLoading || isLoadingOrdersExport} />

            <FullScreenOverlay show={showOrderPreview}>
                <OrderPreview onCloseClick={() => setShowOrderPreview(false)} orderDetails={orderDetails} />
            </FullScreenOverlay>

            {savedCheckReview && <FrontSystemsChangePopup mode="saved-pre-export" review={savedCheckReview} onClose={closeSavedCheck} />}
            {frontSystemsReview && <FrontSystemsChangePopup review={frontSystemsReview} onClose={closeFrontSystemsReview}
                onUpdate={updateManualCheck} updateEligible={frontSystemsReview?.manualReport?.available === true && !["running", "uncertain"].includes(frontSystemsReview?.manualReport?.state)}
                onExport={continueExistingExport} exportEligible={!!isCurrentExportWorkflow(exportWorkflowRef.current) && !exportWorkflowRef.current?.invalidated}
                exportPending={isLoadingOrdersExport} processing={exportWorkflowRef.current?.processing === true} />}

            {viewLayoutStyle === "Grid" && (
                <InfinityGridOrdersCards
                    onAutomaticCheckRowsChange={onAutomaticCheckRowsChange}
                    onCheckClick={onClickManualCheck}
                    ref={gridRef}
                    manualReports
                    usePremiumSummary
                    minColumnWidth="288px"
                    limit={12}
                    queryKey={gridQueryKey}
                    filters={filters}
                    requestEnabled={requestEnabled}
                    onDeleteClick={onClickDelete}
                    onViewClick={onClickPreview}
                    onExportClick={onClickExport}
                    onRulesClick={onClickSetRules}
                />
            )}

            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="Orders"
                    columns={listColumns}
                    api={{
                        endpoint: "getBulkOrdersData",
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            limit: listNumRows,
                            filters: filters
                        },
                        useCommonPostRequest: true,
                        commonContext: "SupplierOrders",
                        onSuccess: data => {
                            const items = data?.result ?? [];
                            const mappedItems = items?.map(item => {
                                return { ...item, mappedStatus: getMappedStatus(item?.status) };
                            });
                            return mappedItems;
                        }
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        includeActions: true,
                        extraActions: [
                            {
                                title: "Delete",
                                onClick: onClickDelete,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("delete", item?.status)
                            },
                            {
                                title: "View",
                                onClick: onClickPreview,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("view", item?.status)
                            },
                            {
                                title: "Rules",
                                onClick: onClickSetRules,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("rules", item?.status)
                            },
                            {
                                title: "Export",
                                onClick: onClickExport,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("export", item?.status)
                            }
                        ]
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "mappedStatus",
                        statusColors: orderStatusColors,
                        width: "220px",
                        renderStatus: ({ row, defaultStatusView }) => (
                            <div className="flex align-items-center gap-4px">
                                {defaultStatusView}
                                <OrderMissingSizesTooltip data={row} placement="left" />
                            </div>
                        )
                    }}
                    requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders(
    props,
    onClickRefreshAction,
    onSearch,
    onFiltersLoaded,
    allRetailBrandsList,
    onRetailBrandChange,
    filteredRetailBrand,
    allLabelBrandsList,
    onLabelBrandChange,
    filteredLabelBrand,
    allStatusList,
    onStatusChange,
    filteredStatus
) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Order number, brand" /> }
    ]);

    props.setControlBarCenterBtns([
        { to: "/orders", active: true, disabled: false, title: "ORDERS" },
        { to: "/orders/reorders", active: false, disabled: false, title: "REORDERS" },
        { to: "/orders/archive", active: false, disabled: false, title: "ARCHIVE" }
    ]);

    props.setControlBarRightBtns([
        { to: "/orders", active: true, disabled: false, title: "View", icon: "eye" },
        { to: "/orders/calendar", active: false, disabled: true, title: "Calendar", icon: "calendar" },
        { to: "/orders/details", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <OrdersHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                firstOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: allLabelBrandsList?.length === 1,
                    type: allLabelBrandsList?.length === 1 ? "singleSelect" : "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all label brands",
                        labelAllSelected: "All label brands",
                        labelNoneSelected: "--",
                        allItems: allLabelBrandsList
                    }
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allStatusList,
                    onDropdownChange: onStatusChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all status",
                        labelAllSelected: "All status",
                        labelNoneSelected: "--",
                        allItems: allStatusList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true
                }}
            />
        )
    });
}
// #endregion

// #region EXPORTED FUNCTIONS

export const getMappedStatus = status => {
    const statusObject = DEFAULT_STATUSES_FILTER.find(item => item.value?.toLowerCase() === status?.toLowerCase());
    if (statusObject) return statusObject.name;
    return "--";
};

export const getOrderSpanWithColor = (status, isArchivePage = false) => {
    const mappedStatus = isArchivePage && status?.toLowerCase() === "deleted" ? "Deleted" : getMappedStatus(status);

    return getSpanWithColor(mappedStatus, orderStatusColors);
};
// #endregion

// #region SMALL COMPONENTS
const ChooseDestinationWarehouseContent = ({
    warehouseData,
    warehouses = [],
    onWarehouseChange = () => {},
    storeWarehouseID = null
}) => {
    const [warehouseDropdownData, setWarehouseDropdownData] = useState(() => {
        const storeWarehouseExists = storeWarehouseID && warehouses.some(w => w?.id === storeWarehouseID);
        return warehouses.map((warehouse, index) => {
            const isStoreWarehouse = storeWarehouseID && warehouse?.id === storeWarehouseID;
            const isDefault = storeWarehouseExists ? isStoreWarehouse : index === 0;
            return {
                ...warehouse,
                value: warehouse?.id,
                name: warehouse?.name ?? warehouse?.id ?? "--",
                selected: isDefault
            };
        });
    });

    const orderSummary = [
        { label: "Brand:", value: warehouseData?.labelBrandName ?? "--" },
        { label: "Order:", value: warehouseData?.orderNumber ?? "--" },
        { label: "Barcodes:", value: warehouseData?.totalBarcodes ?? "--" },
        { label: "Items:", value: warehouseData?.totalOrderProducts ?? warehouseData?.totalItems ?? "--" },
        { label: "Total amount:", value: formatPrice(warehouseData?.totalPriceIncludingVAT, "kr", "--") }
    ];

    const onDestinationWarehouseChange = data => {
        if (isChecking(warehouseData)) return;
        setWarehouseDropdownData(prev =>
            prev.map(warehouse => ({
                ...warehouse,
                selected: warehouse?.value === data?.value
            }))
        );

        const selectedWarehouse = warehouses.find(
            warehouse => warehouse?.id === data?.value || `${warehouse?.id}` === `${data?.value}`
        );

        onWarehouseChange(selectedWarehouse);
    };

    return (
        <StyledPopup className="destination-warehouse-popup-content">
            <div className="warehouse-popup-description">
                <span className="warehouse-popup-description-main">
                    Choose the warehouse where this order should be exported in the POS.
                </span>
                <span className="warehouse-popup-description-note">
                    If any items don&apos;t already exist in your system, they&apos;ll be created automatically. Stock
                    will remain pending until delivery.
                </span>
            </div>

            <div className="warehouse-order-summary">
                {orderSummary.map(item => (
                    <div key={item.label} className="warehouse-order-summary-row">
                        <span className="warehouse-order-summary-label">{item.label} </span>
                        <span className="warehouse-order-summary-value">{item.value}</span>
                    </div>
                ))}
            </div>

            <div className="warehouse-dropdown-wrapper">
                <FormField
                    disabled={isChecking(warehouseData)}
                    type="dropdown"
                    dropdownData={warehouseDropdownData}
                    varName="destinationWarehouse"
                    isDropdownSearchable={warehouseDropdownData?.length > 6}
                    isDropdownMultiselect={false}
                    placeholder="Select warehouse"
                    onDropdownChange={onDestinationWarehouseChange}
                    formFieldColumSize={1}
                    requiredType="none"
                    styling={{
                        formFieldHeight: "37px",
                        formFieldWidth: "100%",
                        dropdownOverlayTop: "45px"
                    }}
                    disableTooltip={true}
                    disableAssistiveText={true}
                />
            </div>
        </StyledPopup>
    );
};
// #endregion

// Match Archive's guard: child initialization can notify an empty selection
// before the organizer has initialized its selected options.
const OrdersHeader = props => {
    const statusFilterReadyRef = useRef(false);
    useEffect(() => {
        statusFilterReadyRef.current = true;
        return () => { statusFilterReadyRef.current = false; };
    }, []);
    const onStatusChange = (selections, dropdownDataWithSelections) => {
        if (!statusFilterReadyRef.current) return;
        props.secondOrganize.onDropdownChange(selections, dropdownDataWithSelections);
    };
    return <MainWindowHeader {...props} premium secondOrganize={{ ...props.secondOrganize, onDropdownChange: onStatusChange }} />;
};
